/**
 * Kitchen audio — FR-KDS-3.
 *
 * Tones are synthesised with the Web Audio API rather than loaded as files: no
 * network dependency, no asset to go missing on a tablet that has been running
 * for a week, and the alarm still works when the connection drops.
 *
 * Browsers refuse to start audio without a user gesture, which is why the KDS
 * has an explicit "start shift" tap — `arm()` is called from that handler.
 */

type Tone = { frequency: number; durationMs: number; gain?: number }

const CHIME: Tone[] = [
  { frequency: 880, durationMs: 120 },
  { frequency: 1320, durationMs: 180 },
]

/** Deliberately harsher and repeated: this one must interrupt a busy kitchen. */
const ALARM: Tone[] = [
  { frequency: 660, durationMs: 220, gain: 0.35 },
  { frequency: 440, durationMs: 220, gain: 0.35 },
  { frequency: 660, durationMs: 220, gain: 0.35 },
]

export class KitchenSound {
  private context: AudioContext | null = null

  get isArmed(): boolean {
    return this.context !== null && this.context.state === 'running'
  }

  /** Must be called from a user gesture, or the browser will refuse. */
  async arm(): Promise<boolean> {
    try {
      const AudioContextImpl =
        window.AudioContext ??
        (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (!AudioContextImpl) return false

      this.context ??= new AudioContextImpl()
      if (this.context.state === 'suspended') await this.context.resume()

      return this.context.state === 'running'
    } catch {
      return false
    }
  }

  newOrder(): void {
    this.play(CHIME)
  }

  overdue(): void {
    this.play(ALARM)
  }

  private play(tones: Tone[]): void {
    const context = this.context
    if (!context || context.state !== 'running') return

    let startAt = context.currentTime
    for (const tone of tones) {
      const oscillator = context.createOscillator()
      const gain = context.createGain()

      oscillator.type = 'sine'
      oscillator.frequency.value = tone.frequency

      const duration = tone.durationMs / 1000
      const peak = tone.gain ?? 0.25

      // Ramped rather than square-edged, so it does not click on cheap speakers.
      gain.gain.setValueAtTime(0.0001, startAt)
      gain.gain.exponentialRampToValueAtTime(peak, startAt + 0.01)
      gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration)

      oscillator.connect(gain).connect(context.destination)
      oscillator.start(startAt)
      oscillator.stop(startAt + duration)

      startAt += duration
    }
  }
}

export const kitchenSound = new KitchenSound()
